import http from '../client';
import { errorHandler } from '../errorHandler';
import type { ApiResponse, User } from '../types';

interface GetAllUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  approval?: 'pending' | 'approved';
}

interface PaginatedUsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const userApi = {
  getAllUsers: async (
    params?: GetAllUsersParams,
  ): Promise<ApiResponse<PaginatedUsersResponse>> => {
    try {
      // Build query string from params
      const queryParams = new URLSearchParams();

      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }

      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      if (params?.search) {
        queryParams.append('search', params.search);
      }

      if (params?.role) {
        queryParams.append('role', params.role);
      }

      if (params?.status) {
        queryParams.append('status', params.status);
      }

      if (params?.approval) {
        queryParams.append('approval', params.approval);
      }

      const queryString = queryParams.toString();
      const url = `/admin/all-users${queryString ? `?${queryString}` : ''}`;

      const response = await http.get(url);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateCanModerateComments: async (
    userId: number | string,
    canModerateComments: boolean,
  ): Promise<ApiResponse<User>> => {
    try {
      const response = await http.put(
        `/users/${userId}/can-moderate-comments`,
        { canModerateComments },
      );
      const userData = response.data?.user || response.data;
      return {
        success: true,
        data: userData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateCanApprove: async (
    userId: number | string,
    canApprove: boolean,
  ): Promise<ApiResponse<User>> => {
    try {
      const response = await http.put(`/users/${userId}/can-approve`, {
        canApprove,
      });
      // Handle response structure - backend returns { success, message, user }
      const userData = response.data?.user || response.data;
      return {
        success: true,
        data: userData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getNewUsersCount: async (
    days = 7,
  ): Promise<ApiResponse<{ count: number }>> => {
    try {
      const response = await http.get(
        `/admin/users/new-count${days !== 7 ? `?days=${days}` : ''}`,
      );
      return {
        success: true,
        data: response.data?.data ?? response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
};
